<template>
  <div v-if="isAuthenticated" class="token-status">
    <div class="dropdown">
      <button 
        class="btn btn-outline-light dropdown-toggle d-flex align-items-center position-relative" 
        type="button" 
        data-bs-toggle="dropdown" 
        aria-expanded="false"
      >
        <i class="fas fa-user-circle me-2"></i>
        <span class="d-none d-md-inline">{{ user?.name || user?.username || 'Usuario' }}</span>
        <span v-if="totalNotificaciones > 0" class="badge bg-danger rounded-pill ms-2">
          {{ totalNotificaciones }}
        </span>
      </button>
      
      <ul class="dropdown-menu dropdown-menu-end">
        <li class="dropdown-header">
          <div class="d-flex align-items-center">
            <i class="fas fa-user me-2"></i>
            <div>
              <div class="fw-bold">{{ user?.name || 'Usuario' }}</div>
              <small class="text-muted">{{ user?.email }}</small>
            </div>
          </div>
        </li>
        
        <li><hr class="dropdown-divider"></li>
        
        <!-- Opciones del header -->
        <template v-if="opcionesHeader && opcionesHeader.length > 0">
          <li v-for="(opcion, i) in opcionesHeader" :key="i">
            <RouterLink :to="opcion.page" class="dropdown-item d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <i :class="opcion.icon" class="menu-icon me-2"></i>
                <span>{{ opcion.title }}</span>
              </div>
              <span v-if="opcion.notificacion && opcion.notificacion > 0" class="badge bg-danger rounded-pill ms-2">
                {{ opcion.notificacion }}
              </span>
            </RouterLink>
          </li>
        </template>
        
        <!-- Acción Acerca de -->
        <li>
          <a class="dropdown-item d-flex align-items-center" @click="toggleModalAcercade">
            <i class="fa-solid fa-book menu-icon me-2"></i>
            <span>Acerca de</span>
          </a>
        </li>
        
        <li><hr class="dropdown-divider"></li>
        
        
        <li>
          <a class="dropdown-item text-danger d-flex align-items-center" @click="handleLogout" :disabled="isLoading">
            <i class="fas fa-sign-out-alt menu-icon me-2"></i>
            <span>Cerrar sesión</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { computed, toRefs } from 'vue'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'TokenStatus',
  props: {
    opcionesHeader: {
      type: Array,
      default: () => []
    },
    mostrarModalAcercade: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-modal-acercade'],
  setup(props, { emit }) {
    const { 
      user, 
      isAuthenticated, 
      isLoading, 
      minutesUntilExpiry, 
      tokenExpiry, 
      logout, 
      refreshToken: refreshTokenAction 
    } = useAuth()

    // Hacer las props reactivas
    const { opcionesHeader } = toRefs(props)

    // Computed para calcular el total de notificaciones
    const totalNotificaciones = computed(() => {
      if (!opcionesHeader.value || !Array.isArray(opcionesHeader.value)) return 0
      return opcionesHeader.value.reduce((total, opcion) => {
        const notif = opcion.notificacion
        return total + (notif && typeof notif === 'number' && notif > 0 ? notif : 0)
      }, 0)
    })

    // Computed properties para el estado del token
    const statusBadgeClass = computed(() => {
      if (minutesUntilExpiry.value > 30) {
        return 'bg-success'
      } else if (minutesUntilExpiry.value > 10) {
        return 'bg-warning'
      } else {
        return 'bg-danger'
      }
    })

    const statusText = computed(() => {
      if (minutesUntilExpiry.value > 30) {
        return 'Activa'
      } else if (minutesUntilExpiry.value > 10) {
        return 'Por expirar'
      } else {
        return 'Expirando'
      }
    })

    const timeRemainingText = computed(() => {
      const minutes = minutesUntilExpiry.value
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60)
        const remainingMinutes = minutes % 60
        return `${hours}h ${remainingMinutes}m`
      } else {
        return `${minutes}m`
      }
    })

    const expiryTimeText = computed(() => {
      if (!tokenExpiry.value) return 'N/A'
      
      const date = new Date(tokenExpiry.value)
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      })
    })

    // Funciones
    const handleLogout = async () => {
      if (confirm('¿Estás seguro de que quieres cerrar sesión?')) {
        await logout()
      }
    }

    const refreshToken = async () => {
      try {
        await refreshTokenAction()
      } catch (error) {
        console.error('Error al renovar token:', error)
      }
    }

    const toggleModalAcercade = () => {
      emit('toggle-modal-acercade')
    }

    return {
      user,
      isAuthenticated,
      isLoading,
      minutesUntilExpiry,
      statusBadgeClass,
      statusText,
      timeRemainingText,
      expiryTimeText,
      totalNotificaciones,
      handleLogout,
      refreshToken,
      toggleModalAcercade
    }
  }
}
</script>

<style scoped>
.token-status {
  position: relative;
}

.dropdown-toggle {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transition: all 0.3s ease;
}

.dropdown-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.dropdown-toggle:focus {
  box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.25);
}

.dropdown-menu {
  min-width: 280px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.dropdown-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.dropdown-item-text {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.dropdown-item {
  transition: background-color 0.15s ease-in-out;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
  min-width: 20px;
  text-align: center;
}

.badge.rounded-pill {
  padding: 0.25rem 0.6rem;
  font-size: 0.7rem;
}

.dropdown-toggle .badge {
  font-size: 0.65rem;
  padding: 0.2rem 0.5rem;
  margin-left: 0.5rem;
}

@media (max-width: 768px) {
  .dropdown-menu {
    min-width: 250px;
  }
}

/* Estilos para alineación de iconos */
.menu-icon {
  width: 16px;
  text-align: center;
  display: inline-block;
}
</style>
